import React from 'react';
import { Carousel, RubusMedia } from '@ygtechnologies/rubus-ui';
import { classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';

import theme from './RoomOptionTwoTierList.module.scss';
import { ConfiguratorOptionDto } from '@/services/configurator';
import { useImageUrls } from '../../../useImageUrls';
import { useIsStackedPreview } from '../../../useConfiguratorLayout';
import { ProductImagePlaceholder } from '@/components/ProductImagePlaceholder';

interface GroupProductPreviewProps {
    /** The group's selected option, or null when nothing is selected yet (shows a prompt instead). */
    option: ConfiguratorOptionDto | null;
    groupName: string;
}

/**
 * Light backdrop over the group list while a group is open. With a selection it shows the product's
 * name + image slider; with nothing selected it shows a centered prompt to pick an option.
 */
export const GroupProductPreview: React.FC<GroupProductPreviewProps> = ({ option, groupName }) => {
    const { getProjectCatalogProductFileUrl, getProjectCatalogProductFileThumbnailUrl } = useImageUrls();
    // Portrait phones/tablets stack the visual into a short, boxed panel: show the image full-bleed as
    // a slider (no name, no thumbnail strip — overlaid dot indicators instead) so it fills the space.
    const fill = useIsStackedPreview();

    if (!option) {
        return (
            <div className={theme.previewOverlay}>
                <div className={theme.previewInner}>
                    <div className={theme.previewEmpty}>
                        <Trans>Nothing selected for {groupName}, select an option from the right.</Trans>
                    </div>
                </div>
            </div>
        );
    }

    const images = option.product?.images ?? [];
    const media: RubusMedia[] = images.map((img) => ({
        type: 'image',
        src: getProjectCatalogProductFileUrl(img.id),
        thumbnailSrc: getProjectCatalogProductFileThumbnailUrl(img.id),
        title: option.productName,
    }));

    return (
        <div className={classnames(theme.previewOverlay, { [theme.previewOverlayFill]: fill })}>
            <div className={classnames(theme.previewInner, { [theme.previewInnerFill]: fill })}>
                {!fill && <div className={theme.previewName}>{option.productName}</div>}
                {media.length > 0 ? (
                    <Carousel
                        className={classnames(theme.previewCarousel, { [theme.previewCarouselFill]: fill })}
                        height={360}
                        mode={fill ? 'indicators' : undefined}
                        src={media}
                    />
                ) : (
                    <div className={theme.previewMissing}>
                        <ProductImagePlaceholder />
                    </div>
                )}
            </div>
        </div>
    );
};
